/**
 * Задачи товара договора под его карточкой во вкладке «Договор» — как в CRM: «Обещали», «Ремонт»,
 * «Химчистка», «Подшив» с текстом, фото и автором; выполненных нет. «+ задача» — сотрудник КЦ
 * ставит задачу сам (**пишет в боевую CRM**, её увидят салон и мастер): одна из четырёх, текст
 * обязателен, такую же открытую вторую не поставить. Фото задач — слайдером (`MediaViewer`) по всем
 * задачам товара. Спеки `docs/specs/2026-10-22-product-tasks-design.md`,
 * `docs/specs/2026-10-22-chat-comment-media-design.md`.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { type MediaItem, MediaThumb, MediaViewer } from "@/components/MediaViewer";
import { type ContractProduct, PRODUCT_TASKS, type ProductTask, putProductTask } from "@/lib/dealContract";
import { btn } from "@/lib/panelBtn";

const pill = "rounded-full px-1.5 py-px text-[11px] font-medium leading-[16px]";
const OPEN = new Set([1, 2]);

const when = (at: string | null) => (at ? `${at.slice(8, 10)}.${at.slice(5, 7)} ${at.slice(11, 16)}` : "");

/**
 * Плашка задачи CRM: цвет — статус задачи (`sTask_status.taskColor`: красный «не выполнено»,
 * оранжевый «в работе»), рядом статус словом. Та же в ленте «Чатов» (`CommentBubble`).
 */
export function TaskPill({ name, color, status }: { name: string; color: string | null; status: string | null }) {
  return (
    <>
      <span
        className={`${pill} ${color ? "text-white" : "border border-border"}`}
        style={color ? { background: color } : undefined}
        title={status ?? undefined}
      >
        {name}
      </span>
      {status && <span className="text-[11px] text-fg-muted">{status.toLowerCase()}</span>}
    </>
  );
}

/** Список задач товара; пусто — ничего. */
export function ProductTaskList({ tasks }: { tasks: ProductTask[] }) {
  const [view, setView] = useState<number | null>(null);
  if (tasks.length === 0) return null;
  // Один слайдер на все фото задач товара: у задачи — начало её фото в общем списке.
  const media: MediaItem[] = [];
  const from = new Map<number, number>();
  for (const t of tasks) {
    from.set(t.id, media.length);
    const meta = [t.name, t.who, when(t.at)].filter(Boolean).join(" · ");
    t.previews.forEach((p, i) => media.push({ kind: "photo", url: t.images[i] ?? p, preview: p, caption: t.text, meta }));
  }
  return (
    <ul className="flex flex-col gap-1.5" data-annot="задачи товара">
      {tasks.map((t) => (
        <li key={t.id} className="rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-2 py-1.5 text-[12px]">
          <div className="flex flex-wrap items-center gap-x-1.5">
            <TaskPill name={t.name} color={t.color} status={t.status_name} />
            <span className="ml-auto text-[11px] text-fg-faint">
              {t.who}
              {t.at ? ` · ${when(t.at)}` : ""}
            </span>
          </div>
          {t.text && <div className="mt-0.5 whitespace-pre-line break-words leading-snug">{t.text}</div>}
          {t.previews.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {t.previews.map((src, i) => {
                const k = (from.get(t.id) ?? 0) + i;
                return <MediaThumb key={src} item={media[k]!} size={48} onClick={() => setView(k)} />;
              })}
            </div>
          )}
        </li>
      ))}
      {view !== null && <MediaViewer items={media} start={view} onClose={() => setView(null)} />}
    </ul>
  );
}

/** «+ задача» и форма постановки на месте. */
export function ProductTaskAdd({ p, dealId, chatId }: { p: ContractProduct; dealId: number; chatId: number | null }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  if (!open)
    return (
      <>
        <button
          type="button"
          className="text-[11.5px] text-accent-text hover:underline"
          title="Поставить задачу товару — запишется в CRM, её увидят салон и мастер"
          onClick={() => (setDone(null), setOpen(true))}
        >
          + задача
        </button>
        {done && (
          <span role="status" className="text-[11.5px] text-ok">
            ✓ {done}
          </span>
        )}
      </>
    );
  return <TaskForm p={p} dealId={dealId} chatId={chatId} onClose={() => setOpen(false)} onDone={(t) => (setDone(t), setOpen(false))} />;
}

function TaskForm({
  p,
  dealId,
  chatId,
  onClose,
  onDone,
}: {
  p: ContractProduct;
  dealId: number;
  chatId: number | null;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const qc = useQueryClient();
  const busy = new Set((p.tasks ?? []).filter((t) => OPEN.has(t.status)).map((t) => t.task));
  const [task, setTask] = useState<number | null>(null);
  const [text, setText] = useState("");
  const field = useRef<HTMLTextAreaElement>(null);
  const first = useRef<HTMLButtonElement>(null);
  useEffect(() => first.current?.focus(), []);
  const save = useMutation({
    mutationFn: () => putProductTask(dealId, p.id, task!, text.trim(), chatId),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["deal-contract", dealId] });
      if (chatId) void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
      onDone(`Задача «${r.name}» записана в CRM`);
    },
  });
  const missing = [!task && "выберите задачу", !text.trim() && "напишите, что нужно сделать"].filter(Boolean) as string[];
  const ready = missing.length === 0 && !save.isPending;
  return (
    <form
      className="flex w-full flex-col gap-2 rounded-[var(--radius-ui)] border border-border bg-bg p-2 text-[12.5px]"
      onSubmit={(e) => (e.preventDefault(), ready && save.mutate())}
      onKeyDown={(e) => e.key === "Escape" && !save.isPending && onClose()}
      data-annot="новая задача товара"
    >
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1 font-medium">Задача товару {p.serial}</div>
        <button type="button" className="grid size-6 place-items-center rounded text-fg-muted hover:bg-bg-hover hover:text-fg" aria-label="Закрыть" title="Закрыть (Esc)" onClick={onClose}>
          <X size={14} aria-hidden />
        </button>
      </div>
      <fieldset disabled={save.isPending} className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Задача">
          {PRODUCT_TASKS.map((t, i) => {
            const exists = busy.has(t.id);
            return (
              <button
                key={t.id}
                ref={i === 0 ? first : undefined}
                type="button"
                role="radio"
                aria-checked={task === t.id}
                disabled={exists}
                title={exists ? "У товара уже есть такая открытая задача" : undefined}
                className={`h-7 rounded-full border px-2.5 text-[12px] disabled:cursor-not-allowed disabled:opacity-50 ${
                  task === t.id ? "border-accent bg-accent text-white" : "border-border hover:border-border-strong hover:bg-bg-hover"
                }`}
                onClick={() => (setTask(t.id), field.current?.focus())}
              >
                {t.name}
              </button>
            );
          })}
        </div>
        <textarea
          ref={field}
          rows={2}
          maxLength={2000}
          className="w-full resize-y rounded-[var(--radius-ui)] border border-border bg-transparent px-2 py-1 leading-snug hover:border-border-strong focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
          placeholder="что нужно сделать — увидят салон и мастер"
          aria-label="Текст задачи"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Ctrl/⌘+Enter — поставить; Enter — новая строка (задачи бывают списком).
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && ready) (e.preventDefault(), save.mutate());
          }}
        />
      </fieldset>
      {save.isError && <div className="text-err">{save.error.message}</div>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" className={btn.primary} disabled={!ready}>
          {save.isPending ? "Записываю в CRM…" : "Поставить задачу"}
        </button>
        <button type="button" className={btn.quiet} onClick={onClose} disabled={save.isPending}>
          Закрыть
        </button>
      </div>
      {missing.length > 0 && <div className="text-[11.5px] text-fg-muted">Чтобы поставить: {missing.join(" · ")}</div>}
    </form>
  );
}
